/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { RentalStatusEnum } from "@/lib/enum/rental-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { formatCurrency } from "@/lib/utility/currency-helper";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatAsFullDateAndFullHour, formatYearsMonthDay } from "@/lib/utility/date-helper";
import { $t } from "@/lib/utility/t";
import rentalService from "@/services/mrfiktiv/services/rental.service";
import {
  MrfiktivCreateRentalDtoGen,
  MrfiktivPartialAddressWithGeoViewModelGen,
  MrfiktivRentalPeriodDtoGen,
  MrfiktivRentalPeriodGen,
  MrfiktivRentalViewModelGen,
  MrfiktivUpdateRentalDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { RentalDataAccessLayer } from "@/store/modules/access-layers/rental.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { ActivityLog } from "./activity-log.entity";
import { AddressWithGeo } from "./address.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IReference, Reference } from "./reference.entity";
import { RentalPeriod } from "./rental-period-start.entity";
import { IReport } from "./report.entity";
import { ISignDocument, SignDocument } from "./sign-document.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";

@IsFilterable
class RentalBase implements IEntity<IRental, MrfiktivRentalViewModelGen> {
  /** Id of the rental object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.id"
  })
  id: string;

  /** Status of the rental */
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.rental.status",
    config: {
      items: Object.values(RentalStatusEnum).map(status => {
        return {
          text: `enums.RentalStatusEnum.${status}`,
          value: status
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  status: RentalStatusEnum;

  partnerId: string;

  /** The userId that created or owns the rental */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.rental.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  /** Title of the rental */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.title"
  })
  title?: string;

  /** Title of the rental */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.title"
  })
  numberplate?: string;

  /** The document ID */
  documentId?: string;
  document?: ISignDocument | null;
  isDocumentLoading = false;

  /** The report ID */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.rental.reportId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-report"
    }
  })
  reportId?: string;
  report: IReport | null = null;

  /** Period of rental (start and end dates) */
  @FilterConfig({
    type: RentalPeriod
  })
  period: MrfiktivRentalPeriodGen;

  _startString = "";

  get start() {
    if (!this.period.start) {
      return "";
    }
    return formatAsFullDateAndFullHour(new Date(this.period.start).getTime());
  }

  set start(date: string) {
    if (date) {
      this.period.start = new Date(date).getTime();
    }

    this._startString = date;
  }

  _endString = "";

  get end() {
    if (!this.period.end) {
      return "";
    }
    return formatAsFullDateAndFullHour(new Date(this.period.end).getTime());
  }

  set end(date: string) {
    if (date) {
      this.period.end = new Date(date).getTime();
    }

    this._endString = date;
  }

  /** Is pickup service */
  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.rental.isPickup"
  })
  isPickup?: boolean;

  /** Pickup location */
  pickupLocation?: MrfiktivPartialAddressWithGeoViewModelGen | null;

  /** Delivery location */
  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.rental.pickupDate"
  })
  pickupDate?: string;

  get pickupDateFormable(): string {
    if (!this.pickupDate) {
      return "";
    }
    return formatAsFullDateAndFullHour(new Date(this.pickupDate).getTime());
  }
  set pickupDateFormable(date: string) {
    if (date) {
      this.pickupDate = new Date(date).toISOString();
    }

    this.pickupDate = date;
  }

  /** Is delivery service */
  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.rental.isDelivery"
  })
  isDelivery?: boolean;

  /** Delivery location */
  deliveryLocation?: MrfiktivPartialAddressWithGeoViewModelGen | null;

  /** Delivery location */
  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.rental.deliveryDate"
  })
  deliveryDate?: string;

  get deliveryDateFormable(): string {
    if (!this.deliveryDate) {
      return "";
    }
    return formatAsFullDateAndFullHour(new Date(this.deliveryDate).getTime());
  }
  set deliveryDateFormable(date: string) {
    if (date) {
      this.deliveryDate = new Date(date).toISOString();
    }

    this.deliveryDate = date;
  }

  /** Daily rental rate */
  dailyRate: number;

  /** Additional notes */
  notes?: string;

  /** Return date */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.returnDate"
  })
  returnDate?: string;

  get returnDateFormable(): string {
    if (!this.returnDate) {
      return "";
    }
    return formatAsFullDateAndFullHour(new Date(this.returnDate).getTime());
  }
  set returnDateFormable(date: string) {
    if (date) {
      this.returnDate = new Date(date).toISOString();
    }

    this.returnDate = date;
  }

  /** Rate (daily, weekly, etc.) */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.rate"
  })
  rate?: string;

  /** Vehicle group */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.vehicleGroup"
  })
  vehicleGroup?: string;

  /** Vehicle model */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.rental.vehicleModel"
  })
  vehicleModel?: string;

  /** Mileage rate */
  mileageRate?: number;

  /** Free mileage allowance */
  freeMileageAllowance?: number;

  /** Other cost */
  otherCost?: number;

  /** Cost covered by customer */
  costCoveredByCustomer?: number;

  /** Cost covered by insurance */
  costCoveredByInsurance?: number;

  /** Full comprehensive deductible */
  fullComprehensiveDeductible?: number;

  /** Partial coverage deductible */
  partialCoverageDeductible?: number;

  /** Mileage at start */
  mileageStart?: number;

  /** Mileage at end */
  mileageEnd?: number;

  /** Fuel percentage at start */
  fuelPercentageStart?: number;

  /** Fuel percentage at end */
  fuelPercentageEnd?: number;

  /** The timestamp of the rental */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  /** Buyer */
  @FilterConfig({
    type: InvoiceParty
  })
  buyer: IInvoiceParty;

  /** Seller */
  @FilterConfig({
    type: InvoiceParty
  })
  seller: IInvoiceParty;

  /** References */
  @FilterConfig({
    type: Reference
  })
  refs?: IReference[];

  /** Currency */
  currency: CurrencyEnum;

  /** Assignees */
  @FilterConfig({
    displayName: "objects.rental.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees?: string[];

  loading = false;

  /**
   * Gets the formatted total cost of rental
   */
  get totalCost(): number | undefined {
    if (!this.dailyRate || !this.period) return undefined;

    // Calculate days between start and end date
    const startDate = new Date(this.period.start);
    const endDate = new Date(this.period.end);
    const daysCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    let total = this.dailyRate * daysCount;

    // Add mileage cost if applicable
    if (this.mileageEnd && this.mileageStart && this.mileageRate) {
      const extraMiles = Math.max(0, this.mileageEnd - this.mileageStart - (this.freeMileageAllowance || 0));
      total += extraMiles * this.mileageRate;
    }

    // Add other costs if applicable
    if (this.otherCost) {
      total += this.otherCost;
    }

    return total;
  }

  get totalCostReadable(): string {
    if (!this.totalCost) {
      return "";
    }
    return formatCurrency(this.totalCost, this.currency);
  }

  get dailyRateReadable(): string {
    if (!this.dailyRate) {
      return "";
    }
    return formatCurrency(this.dailyRate, this.currency);
  }

  get costCoveredByCustomerReadable(): string {
    if (!this.costCoveredByCustomer) {
      return "";
    }
    return formatCurrency(this.costCoveredByCustomer, this.currency);
  }

  get costCoveredByInsuranceReadable(): string {
    if (!this.costCoveredByInsurance) {
      return "";
    }
    return formatCurrency(this.costCoveredByInsurance, this.currency);
  }

  get periodInDays() {
    const startDate = new Date(this.period.start);
    const endDate = new Date(this.period.end);
    const daysCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysCount;
  }

  /**
   * Maps status to color
   */
  static get statusColorMap() {
    return new Map([
      [RentalStatusEnum.DRAFT, "grey"],
      [RentalStatusEnum.REQUESTED, "orange lighten-2"],
      [RentalStatusEnum.CONFIRMED, "green lighten-2"],
      [RentalStatusEnum.ACTIVE, "green"],
      [RentalStatusEnum.COMPLETED, "deep-purple accent-2"],
      [RentalStatusEnum.CANCELLED, "red"]
    ]);
  }

  /**
   * Maps status to icon
   */
  static get statusIconMap() {
    return new Map([
      [RentalStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RentalStatusEnum.REQUESTED, "mdi-file-document-outline"],
      [RentalStatusEnum.CONFIRMED, "mdi-check-circle-outline"],
      [RentalStatusEnum.ACTIVE, "mdi-car"],
      [RentalStatusEnum.COMPLETED, "mdi-flag-checkered"],
      [RentalStatusEnum.CANCELLED, "mdi-close-circle-outline"]
    ]);
  }

  /**
   * Gets the color for the current status
   */
  get statusColor() {
    return RentalBase.statusColorMap.get(this.status);
  }

  /**
   * Gets the icon for the current status
   */
  get statusIcon() {
    return RentalBase.statusIconMap.get(this.status);
  }

  /**
   * Gets the readable status text
   */
  get statusReadable() {
    return $t(`enums.RentalStatusEnum.${this.status}`);
  }

  /**
   * Is the rental in a completed state (either completed or cancelled)
   */
  get isCompleted() {
    return [RentalStatusEnum.DRAFT, RentalStatusEnum.COMPLETED, RentalStatusEnum.CANCELLED].includes(this.status);
  }

  get isDue() {
    return !this.isCompleted && new Date(this.period.end) < new Date();
  }

  /**
   * Can the rental be edited
   */
  get canEdit() {
    return ![RentalStatusEnum.COMPLETED, RentalStatusEnum.CANCELLED].includes(this.status);
  }

  /**
   * Can the rental be updated
   */
  get canUpdate() {
    return [RentalStatusEnum.DRAFT, RentalStatusEnum.REQUESTED].includes(this.status);
  }

  constructor(data: Partial<MrfiktivRentalViewModelGen | IRental>) {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";
    this.title = data?.title;
    this.documentId = data?.documentId;
    this.reportId = data?.reportId;

    this.numberplate = data?.numberplate;

    // Handle period data
    this.period = data?.period ?? { start: Date.now(), end: Date.now() + 86400000 }; // Default is 1 day rental

    this.isPickup = data?.isPickup;
    this.pickupLocation = data?.pickupLocation ? new AddressWithGeo(data.pickupLocation) : null;
    this.pickupDate = data?.pickupDate ? new Date(data.pickupDate).toISOString() : undefined;

    this.isDelivery = data?.isDelivery;
    this.deliveryLocation = data?.deliveryLocation ? new AddressWithGeo(data.deliveryLocation) : null;
    this.deliveryDate = data?.deliveryDate ? new Date(data.deliveryDate).toISOString() : undefined;

    this.dailyRate = data?.dailyRate ?? 0;
    this.notes = data?.notes;
    this.returnDate = data?.returnDate;
    this.rate = data?.rate;
    this.vehicleGroup = data?.vehicleGroup;
    this.vehicleModel = data?.vehicleModel;
    this.mileageRate = data?.mileageRate;
    this.freeMileageAllowance = data?.freeMileageAllowance;
    this.otherCost = data?.otherCost;

    this.costCoveredByCustomer = data?.costCoveredByCustomer;
    this.costCoveredByInsurance = data?.costCoveredByInsurance;
    this.fullComprehensiveDeductible = data?.fullComprehensiveDeductible;
    this.partialCoverageDeductible = data?.partialCoverageDeductible;

    this.mileageStart = data?.mileageStart;
    this.mileageEnd = data?.mileageEnd;
    this.fuelPercentageStart = data?.fuelPercentageStart;
    this.fuelPercentageEnd = data?.fuelPercentageEnd;

    this.timestamp = new Timestamp(data?.timestamp);
    this.buyer = data?.buyer
      ? new InvoiceParty(data.buyer)
      : new InvoiceParty({
          name: "",
          address: {
            street: "",
            city: "",
            zip: "",
            countryCode: "DE",
            state: ""
          }
        });
    this.seller = data?.seller
      ? new InvoiceParty(data.seller)
      : new InvoiceParty({
          name: "",
          address: {
            street: "",
            city: "",
            zip: "",
            countryCode: "DE",
            state: ""
          }
        });
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map(ref => new Reference(ref)));

    this.currency = (data?.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.assignees = data?.assignees ?? [];
    this.status = (data?.status ?? RentalStatusEnum.DRAFT) as RentalStatusEnum;
  }

  /**
   * Map data from API model
   */
  map(data: MrfiktivRentalViewModelGen | IRental): void {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.documentId = data.documentId;
    this.reportId = data.reportId;

    this.title = data.title;

    this.period = data.period;
    this.returnDate = data.returnDate;

    this.isPickup = data.isPickup;
    this.pickupLocation = data?.pickupLocation ? new AddressWithGeo(data.pickupLocation) : null;
    this.pickupDate = data.pickupDate ? new Date(data.pickupDate).toISOString() : undefined;

    this.isDelivery = data.isDelivery;
    this.deliveryLocation = data?.deliveryLocation ? new AddressWithGeo(data.deliveryLocation) : null;
    this.deliveryDate = data.deliveryDate ? new Date(data.deliveryDate).toISOString() : undefined;

    this.currency = data.currency as CurrencyEnum;
    this.dailyRate = data.dailyRate;
    this.mileageRate = data.mileageRate;
    this.freeMileageAllowance = data.freeMileageAllowance;
    this.otherCost = data.otherCost;

    this.mileageStart = data.mileageStart;
    this.mileageEnd = data.mileageEnd;
    this.fuelPercentageStart = data.fuelPercentageStart;
    this.fuelPercentageEnd = data.fuelPercentageEnd;

    this.fullComprehensiveDeductible = data.fullComprehensiveDeductible;
    this.partialCoverageDeductible = data.partialCoverageDeductible;

    this.costCoveredByCustomer = data.costCoveredByCustomer;
    this.costCoveredByInsurance = data.costCoveredByInsurance;

    this.rate = data.rate;
    this.vehicleGroup = data.vehicleGroup;
    this.vehicleModel = data.vehicleModel;
    this.numberplate = data.numberplate;
    this.notes = data.notes;

    this.timestamp = new Timestamp(data.timestamp);

    this.buyer = new InvoiceParty(data.buyer);
    this.seller = new InvoiceParty(data.seller);

    this.refs = Reference.filterDuplicates((data.refs ?? []).map(ref => new Reference(ref)));
    this.assignees = data.assignees ?? [];
    this.status = data.status as RentalStatusEnum;

    this._startString = formatYearsMonthDay(new Date(data.period?.start ?? new Date()));
    this._endString = formatYearsMonthDay(new Date(data.period?.end ?? new Date()));
  }

  /**
   * Create a new rental
   */
  async create(): Promise<this> {
    this.loading = true;
    try {
      const rentalDto = this.createRentalDto();

      const res = await rentalService.create(this.partnerId, rentalDto);

      this.map(res);
      RentalDataAccessLayer.set(this);
    } finally {
      this.loading = false;
    }

    return this;
  }

  /**
   * Fetch a rental by ID
   */
  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await rentalService.getOne(this.partnerId, this.id);
      this.map(res);
      RentalDataAccessLayer.set(this);
    } catch (e) {
      RentalDataAccessLayer.delete(this);
      throw e;
    } finally {
      this.loading = false;
    }
    return this;
  }

  async fetchDocument() {
    if (!this.documentId) {
      this.document = null;

      return;
    }
    try {
      this.isDocumentLoading = true;
      this.document = await new SignDocument({ id: this.documentId, partnerId: this.partnerId }).fetch();
    } finally {
      this.isDocumentLoading = false;
    }
  }

  /**
   * Update a rental partially
   */
  async updatePartial(dto: MrfiktivUpdateRentalDtoGen): Promise<this> {
    const res = await rentalService.update(this.partnerId, this.id, dto);
    this.map(res);
    RentalDataAccessLayer.set(this);
    return this;
  }

  /**
   * Update a rental
   */
  async update(): Promise<this> {
    this.loading = true;
    try {
      const rentalDto = this.updateRentalDto();
      await this.updatePartial(rentalDto);
    } finally {
      this.loading = false;
    }
    return this;
  }

  /**
   * Delete a rental
   */
  async delete(): Promise<void> {
    this.loading = true;
    try {
      const res = await rentalService.delete(this.partnerId, this.id);
      this.map(res);
      RentalDataAccessLayer.delete(this);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Update the status of a rental
   */
  async updateStatus(): Promise<this> {
    this.loading = true;
    try {
      const updated = await rentalService.updateStatus(this.partnerId, this.id);
      this.map(updated);
    } finally {
      this.loading = false;
    }
    return this;
  }

  async uploadDocument(file: File): Promise<ISignDocument> {
    const refs = [];

    if (!this.id) {
      throw new Error("Rental ID is required to upload a rental document.");
    }

    if (this.reportId) {
      refs.push(new Reference({ refId: this.reportId, refType: ResourceEnum.REPORT }));
    }

    refs.push(new Reference({ refId: this.id, refType: ResourceEnum.RENTAL }));

    const signDocument = new SignDocument({
      name: file.name,
      title: file.name,
      isTemplate: false,
      folder: "/rental/",
      refs: refs,
      file: file,
      partnerId: this.partnerId
    });

    return signDocument.create();
  }

  /**
   * Create rental DTO for API
   */
  private createRentalDto(): MrfiktivCreateRentalDtoGen {
    return {
      title: this.title || "",
      status: this.status as "DRAFT" | "REQUESTED" | "CONFIRMED" | "ACTIVE" | "COMPLETED" | "CANCELLED",
      period: this.period as MrfiktivRentalPeriodDtoGen,
      documentId: this.documentId,
      reportId: this.reportId,
      seller: this.seller?.dto,
      buyer: this.buyer?.dto,
      refs: Reference.filterDuplicates(this.refs || []),
      currency: this.currency,
      assignees: this.assignees,
      isPickup: this.isPickup,
      pickupLocation: this.isPickup ? this.pickupLocation : undefined,
      pickupDate: this.isPickup ? this.pickupDate : undefined,
      isDelivery: this.isDelivery,
      deliveryLocation: this.isDelivery ? this.deliveryLocation : undefined,
      deliveryDate: this.isDelivery ? this.deliveryDate : undefined,
      dailyRate: this.dailyRate,
      notes: this.notes,
      returnDate: this.returnDate,
      rate: this.rate,
      vehicleGroup: this.vehicleGroup,
      vehicleModel: this.vehicleModel,
      mileageRate: this.mileageRate,
      numberplate: this.numberplate,
      freeMileageAllowance: this.freeMileageAllowance,
      otherCost: this.otherCost,
      costCoveredByCustomer: this.costCoveredByCustomer,
      costCoveredByInsurance: this.costCoveredByInsurance,
      fullComprehensiveDeductible: this.fullComprehensiveDeductible,
      partialCoverageDeductible: this.partialCoverageDeductible,
      mileageStart: this.mileageStart,
      mileageEnd: this.mileageEnd,
      fuelPercentageStart: this.fuelPercentageStart,
      fuelPercentageEnd: this.fuelPercentageEnd
    };
  }

  /**
   * Update rental DTO for API
   */
  private updateRentalDto(): MrfiktivUpdateRentalDtoGen {
    return {
      title: this.title,
      numberplate: this.numberplate,
      status: this.status as "DRAFT" | "REQUESTED" | "CONFIRMED" | "ACTIVE" | "COMPLETED" | "CANCELLED",
      period: this.period as MrfiktivRentalPeriodDtoGen,
      documentId: this.documentId,
      reportId: this.reportId,
      seller: this.seller?.dto,
      buyer: this.buyer?.dto,
      refs: Reference.filterDuplicates(this.refs || []),
      currency: this.currency,
      assignees: this.assignees,
      isPickup: this.isPickup,
      pickupLocation: this.isPickup ? this.pickupLocation : null,
      pickupDate: this.isPickup ? (this.pickupDate ? new Date(this.pickupDate)?.toISOString() : undefined) : null,
      isDelivery: this.isDelivery,
      deliveryLocation: this.isDelivery ? this.deliveryLocation : null,
      deliveryDate: this.isDelivery
        ? this.deliveryDate
          ? new Date(this.deliveryDate)?.toISOString()
          : undefined
        : null,
      dailyRate: this.dailyRate,
      notes: this.notes,
      returnDate: this.returnDate,
      rate: this.rate,
      vehicleGroup: this.vehicleGroup,
      vehicleModel: this.vehicleModel,
      mileageRate: this.mileageRate,
      freeMileageAllowance: this.freeMileageAllowance,
      otherCost: this.otherCost,
      costCoveredByCustomer: this.costCoveredByCustomer,
      costCoveredByInsurance: this.costCoveredByInsurance,
      fullComprehensiveDeductible: this.fullComprehensiveDeductible,
      partialCoverageDeductible: this.partialCoverageDeductible,
      mileageStart: this.mileageStart,
      mileageEnd: this.mileageEnd,
      fuelPercentageStart: this.fuelPercentageStart,
      fuelPercentageEnd: this.fuelPercentageEnd
    };
  }

  /**
   * Create an activity log for assignee changes
   * @param newAssignees The ids of the new assignees of the ticket
   * @param activityType Whether the assignee was added or removed
   */
  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: ResourceEnum.RENTAL,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }
}

type IRental = RentalBase;
const Rental = Filter.createForClass(RentalBase);

export { IRental, Rental };
